import { useState } from 'react';
import type { FilterState, FilterPreset } from '../types';
import { saveFilterPreset, loadFilterPresets, buildExportUrl } from '../api/client';

interface FilterPanelProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
}

const EVENT_TYPES = [
  'received', 'sent', 'replied', 'forwarded',
  'moved', 'deleted', 'attachment-added', 'flagged',
];

const SENSITIVITY_OPTIONS = ['normal', 'personal', 'private', 'confidential'];

export default function FilterPanel({ filters, onChange }: FilterPanelProps) {
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [showPresets, setShowPresets] = useState(false);

  function update(partial: Partial<FilterState>) {
    onChange({ ...filters, ...partial });
  }

  function clearAll() {
    onChange({});
  }

  async function handleSavePreset() {
    if (!presetName.trim()) return;
    await saveFilterPreset(presetName, filters);
    setPresetName('');
    alert('Filter preset saved!');
  }

  async function handleLoadPresets() {
    const p = await loadFilterPresets();
    setPresets(p);
    setShowPresets(true);
  }

  function applyPreset(preset: FilterPreset) {
    onChange(preset.filters);
    setShowPresets(false);
  }

  return (
    <aside style={styles.panel}>
      <h2 style={styles.heading}>Filters</h2>

      <label style={styles.label}>Mailbox</label>
      <input
        style={styles.input}
        type="email"
        placeholder="user@contoso.com"
        value={filters.mailbox || ''}
        onChange={(e) => update({ mailbox: e.target.value })}
      />

      <label style={styles.label}>Date From</label>
      <input
        style={styles.input}
        type="datetime-local"
        value={filters.dateFrom || ''}
        onChange={(e) => update({ dateFrom: e.target.value })}
      />

      <label style={styles.label}>Date To</label>
      <input
        style={styles.input}
        type="datetime-local"
        value={filters.dateTo || ''}
        onChange={(e) => update({ dateTo: e.target.value })}
      />

      <label style={styles.label}>Sender(s)</label>
      <input
        style={styles.input}
        type="text"
        placeholder="Comma-separated emails"
        value={filters.senders || ''}
        onChange={(e) => update({ senders: e.target.value })}
      />

      <label style={styles.label}>Subject keyword</label>
      <input
        style={styles.input}
        type="text"
        placeholder="Keyword search"
        value={filters.subject || ''}
        onChange={(e) => update({ subject: e.target.value })}
      />

      <label style={styles.label}>Folder</label>
      <input
        style={styles.input}
        type="text"
        placeholder="Inbox, Sent Items…"
        value={filters.folder || ''}
        onChange={(e) => update({ folder: e.target.value })}
      />

      <label style={styles.label}>Sensitivity</label>
      <select
        style={styles.input}
        value={filters.sensitivity || ''}
        onChange={(e) => update({ sensitivity: e.target.value || undefined })}
      >
        <option value="">Any</option>
        {SENSITIVITY_OPTIONS.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      <label style={styles.label}>Event Types</label>
      <div style={styles.checkboxGroup}>
        {EVENT_TYPES.map((t) => {
          const checked = (filters.types || '').split(',').includes(t);
          return (
            <label key={t} style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  const current = filters.types ? filters.types.split(',').filter(Boolean) : [];
                  const updated = e.target.checked
                    ? [...current, t]
                    : current.filter((x) => x !== t);
                  update({ types: updated.join(',') || undefined });
                }}
              />
              {t}
            </label>
          );
        })}
      </div>

      <label style={styles.checkboxLabel}>
        <input
          type="checkbox"
          checked={filters.hasAttachment === true}
          onChange={(e) => update({ hasAttachment: e.target.checked ? true : undefined })}
        />
        Has attachment
      </label>

      <div style={styles.buttonRow}>
        <button style={styles.btnSecondary} onClick={clearAll}>Clear</button>
      </div>

      <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />

      <h3 style={styles.subheading}>Export</h3>
      <div style={styles.buttonRow}>
        <a
          href={buildExportUrl(filters, 'csv')}
          download="audit-export.csv"
          style={styles.btnLink}
        >
          CSV
        </a>
        <a
          href={buildExportUrl(filters, 'json')}
          download="audit-export.json"
          style={styles.btnLink}
        >
          JSON
        </a>
      </div>

      <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />

      <h3 style={styles.subheading}>Presets</h3>
      <input
        style={styles.input}
        placeholder="Preset name"
        value={presetName}
        onChange={(e) => setPresetName(e.target.value)}
      />
      <div style={styles.buttonRow}>
        <button style={styles.btnPrimary} onClick={handleSavePreset}>Save</button>
        <button style={styles.btnSecondary} onClick={handleLoadPresets}>Load</button>
      </div>
      {showPresets && presets.length > 0 && (
        <ul style={styles.presetList}>
          {presets.map((p) => (
            <li key={p.id} style={styles.presetItem} onClick={() => applyPreset(p)}>
              {p.name}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    width: 260,
    minWidth: 200,
    background: '#f9fafb',
    borderRight: '1px solid #e5e7eb',
    padding: '20px 16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  heading: { fontSize: 18, fontWeight: 700, marginBottom: 8 },
  subheading: { fontSize: 14, fontWeight: 600, marginBottom: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151' },
  input: {
    width: '100%', padding: '6px 8px', borderRadius: 6,
    border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box',
  },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  buttonRow: { display: 'flex', gap: 8, marginTop: 4 },
  btnPrimary: {
    flex: 1, padding: '6px 12px', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  btnSecondary: {
    flex: 1, padding: '6px 12px', background: '#e5e7eb', color: '#111',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  btnLink: {
    flex: 1, padding: '6px 12px', background: '#10b981', color: '#fff',
    borderRadius: 6, textDecoration: 'none', fontSize: 13, textAlign: 'center',
  },
  presetList: { listStyle: 'none', padding: 0, margin: '4px 0 0' },
  presetItem: {
    padding: '6px 8px', background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 4, cursor: 'pointer', fontSize: 13, marginBottom: 4,
  },
};
